var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class BinaryMoip {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        // other class properties and constructor
        this.deviceList = {};
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetected();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`?Firmware`, /\?Firmware=([\d\.]+)/i, tags);
                return match[1];
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const rx = key.slice(3); // get past the rx.
                const tx = value.slice(3); // get past the tx.
                const match = yield this.socket.send(`!Switch=${tx},${rx}`, // !Switch=TX,RX
                /(ok|error)/i, tags, { sendTimeoutMs: 3000 } // switching responses take a little more time
                );
                return match[1].toLowerCase() === 'ok'
                    ? value
                    : this.state.videoMatrixRoutes.values[key];
            }),
            asyncHandlers: {
                regExp: /~Receivers=((?:\d+:\d+,?)+)/i,
                callback: (match) => {
                    this.parseStatus(match[1]);
                },
            },
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.send(`Ping`, /#error/i, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            yield this.discoverChannels(['configure']);
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            // Get the initial matrix routings after the channels have been detected.
            yield this.getVideoRoutings(['control']);
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            // The API documentation indicates there is a username and password prompt.
            const loginInitializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    unterminatedRegExp: /Username:/i,
                    next: /Username:/i,
                },
                {
                    errorCode: 'authFailed',
                    send: (cred) => {
                        var _a;
                        return (_a = cred.username) !== null && _a !== void 0 ? _a : 'binary';
                    },
                    unterminatedRegExp: /Password:/i,
                    next: /Password:/i,
                    fail: /Wrong|incorrect/i,
                },
                {
                    errorCode: 'authFailed',
                    send: (cred) => {
                        var _a;
                        return (_a = cred.password) !== null && _a !== void 0 ? _a : 'binary';
                    },
                    next: /Successfully/i,
                    fail: /Wrong|incorrect/i,
                },
            ];
            // Our testing shows that there is no password prompt, so send a command to confirm
            // connection.
            const noLoginInitializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    send: '?Devices',
                    succeed: /\?Devices=\d+,\d+/i,
                },
            ];
            const initializerSteps = [loginInitializerSteps, noLoginInitializerSteps];
            let lastStatus = 'verifyFailed';
            let status = 'verifyFailed';
            for (const step of initializerSteps) {
                status = yield CommandSocketInitializer.initialize(this.socket, step, credentials);
                if (status === 'connected') {
                    return status;
                }
                else if (status === 'authFailed') {
                    lastStatus = status; // remember login connection failure
                }
            }
            // If we didn't verify the connection with no login and failed auth with a login required,
            // return failed auth status so user knows to correct login credentials.
            return lastStatus;
        });
    }
    // other class methods
    discoverChannels(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const detectTimeout = this.timeouts.create();
                // Find inputs (transmitters) and outputs (receivers).
                const endPointNames = this.socket.registerAsync(/\?Name=(\d+),(\d+),([\s\S]+)/i, (match) => {
                    const endPointType = match[1] === '1' ? 'tx' : 'rx';
                    const key = match[2];
                    const name = match[3];
                    this.deviceList[`${endPointType}.${key}`] = {
                        key,
                        name,
                        endPointType,
                    };
                    detectTimeout.restart(1000);
                });
                this.socket.sendAsync('?Name=1\n', ['configure', 'tx']); // find inputs
                this.socket.sendAsync('?Name=0\n', ['configure', 'rx']); // find outputs
                detectTimeout.set(() => __awaiter(this, void 0, void 0, function* () {
                    this.socket.unregisterAsync(endPointNames);
                    resolve();
                }), 2000);
            });
        });
    }
    getDetected() {
        const videoInputs = [];
        const videoOutputs = [];
        for (const device of Object.values(this.deviceList)) {
            if (device.endPointType === 'tx') {
                videoInputs.push({ code: `tx.${device.key}`, external: true, name: device.name });
            }
            else {
                videoOutputs.push({ code: `rx.${device.key}`, name: device.name });
            }
        }
        // Add support for unpairing a transmitter from a receiver to undo the existing routing by
        // using input 0.
        videoInputs.push({ code: `tx.0`, external: false, name: 'No Source' });
        return { videoInputs, videoOutputs };
    }
    getVideoRoutings(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send(`?Receivers`, /\?Receivers=((?:\d+:\d+,?)+)/i, tags);
            this.parseStatus(match[1]);
        });
    }
    parseStatus(ack) {
        // Multiple responses can come back as comma separated groups of TX:RX.
        const responses = ack.split(',');
        for (const resp of responses) {
            const [tx, rx] = resp.split(':');
            this.state.videoMatrixRoutes.update(`rx.${rx}`, `tx.${tx}`);
        }
    }
}
// static properties
BinaryMoip.driverType = 'socketDriver';
BinaryMoip.key = '-OMqdIUEKyPLVmCWigfS';
BinaryMoip.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\n'],
        },
        port: 23,
        sendTerminator: '\n',
    },
];
